import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Output,
  Renderer2
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  selector: '[appPullToRefresh]',
  exportAs: 'appPullToRefresh'
})
export class PullToRefreshDirective implements AfterViewInit, OnDestroy {
  @Input() pullToRefreshDisabled = false;
  @Input() pullToRefreshThreshold = 40;
  @Input() pullToRefreshMax = 80;
  @Input() pullToRefreshEnableWheel = false;
  @Output() refresh = new EventEmitter<void>();

  private startY = 0;
  private pullDistance = 0;
  private pulling = false;
  private refreshing = false;
  private spacer: HTMLElement | null = null;
  private scrollElement: HTMLElement | null = null;
  private listeners: Array<() => void> = [];
  private usePointerEvents = false;
  private wheelDistance = 0;
  private wheelResetTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isBrowser: boolean;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setup();
  }

  ngOnDestroy(): void {
    this.listeners.forEach((remove) => remove());
    this.listeners = [];
    if (this.spacer) {
      this.renderer.removeChild(this.el.nativeElement, this.spacer);
    }
  }

  complete(): void {
    this.refreshing = false;
    this.pulling = false;
    this.pullDistance = 0;
    this.updateIndicator(0, false);
  }

  private setup(): void {
    if (!this.isBrowser) {
      return;
    }

    const host = this.el.nativeElement;
    this.renderer.addClass(host, 'ptr-container');
    const style = getComputedStyle(host);
    if (style.position === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }

    this.scrollElement = this.getScrollElement(host);
    this.spacer = this.createSpacer();
    this.renderer.insertBefore(host, this.spacer, host.firstChild);

    this.zone.runOutsideAngular(() => {
      if (this.pullToRefreshEnableWheel) {
        const onWheel = (event: WheelEvent) => this.handleWheel(event);
        host.addEventListener('wheel', onWheel, { passive: false });
        this.listeners.push(() => host.removeEventListener('wheel', onWheel));
      }

      const hasTouch = typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

      if (hasTouch) {
        const onTouchStart = (event: TouchEvent) => this.handleTouchStart(event);
        const onTouchMove = (event: TouchEvent) => this.handleTouchMove(event);
        const onTouchEnd = () => this.handleTouchEnd();
        host.addEventListener('touchstart', onTouchStart, { passive: true });
        host.addEventListener('touchmove', onTouchMove, { passive: false });
        host.addEventListener('touchend', onTouchEnd, { passive: true });
        host.addEventListener('touchcancel', onTouchEnd, { passive: true });
        this.listeners.push(() => host.removeEventListener('touchstart', onTouchStart));
        this.listeners.push(() => host.removeEventListener('touchmove', onTouchMove));
        this.listeners.push(() => host.removeEventListener('touchend', onTouchEnd));
        this.listeners.push(() => host.removeEventListener('touchcancel', onTouchEnd));
        return;
      }

      this.usePointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;
      if (this.usePointerEvents) {
        const onPointerDown = (event: PointerEvent) => this.handlePointerStart(event);
        const onPointerMove = (event: PointerEvent) => this.handlePointerMove(event);
        const onPointerEnd = () => this.handlePointerEnd();
        host.addEventListener('pointerdown', onPointerDown, { passive: true });
        host.addEventListener('pointermove', onPointerMove, { passive: false });
        host.addEventListener('pointerup', onPointerEnd, { passive: true });
        host.addEventListener('pointercancel', onPointerEnd, { passive: true });
        this.listeners.push(() => host.removeEventListener('pointerdown', onPointerDown));
        this.listeners.push(() => host.removeEventListener('pointermove', onPointerMove));
        this.listeners.push(() => host.removeEventListener('pointerup', onPointerEnd));
        this.listeners.push(() => host.removeEventListener('pointercancel', onPointerEnd));
      }
    });
  }

  private handleTouchStart(event: TouchEvent): void {
    if (this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    const touchTarget = event.target as HTMLElement | null;
    this.scrollElement = this.getScrollElement(this.el.nativeElement, touchTarget);
    if (this.getScrollTop() > 0) {
      return;
    }
    this.pulling = true;
    this.startY = event.touches[0].clientY;
    this.pullDistance = 0;
  }

  private handleTouchMove(event: TouchEvent): void {
    if (!this.pulling || this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    if (this.getScrollTop() > 0) {
      this.updateIndicator(0, false);
      this.pulling = false;
      return;
    }
    const currentY = event.touches[0].clientY;
    const distance = currentY - this.startY;
    if (distance <= 0) {
      this.updateIndicator(0, false);
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.pullDistance = Math.min(distance, this.pullToRefreshMax);
    this.updateIndicator(this.pullDistance, this.pullDistance >= this.pullToRefreshThreshold);
  }

  private handleTouchEnd(): void {
    if (!this.pulling) {
      return;
    }
    this.pulling = false;
    if (this.pullDistance >= this.pullToRefreshThreshold) {
      this.refreshing = true;
      this.updateIndicator(this.pullToRefreshThreshold, true, true);
      this.zone.run(() => this.refresh.emit());
      return;
    }
    this.updateIndicator(0, false);
  }

  private handlePointerStart(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }
    if (this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    const target = event.target as HTMLElement | null;
    this.scrollElement = this.getScrollElement(this.el.nativeElement, target);
    if (this.getScrollTop() > 0) {
      return;
    }
    this.pulling = true;
    this.startY = event.clientY;
    this.pullDistance = 0;
  }

  private handlePointerMove(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }
    if (!this.pulling || this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    if (this.getScrollTop() > 0) {
      this.updateIndicator(0, false);
      this.pulling = false;
      return;
    }
    const distance = event.clientY - this.startY;
    if (distance <= 0) {
      this.updateIndicator(0, false);
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    this.pullDistance = Math.min(distance, this.pullToRefreshMax);
    this.updateIndicator(this.pullDistance, this.pullDistance >= this.pullToRefreshThreshold);
  }

  private handlePointerEnd(): void {
    this.handleTouchEnd();
  }

  private handleWheel(event: WheelEvent): void {
    if (this.pullToRefreshDisabled || this.refreshing) {
      return;
    }
    if (this.getScrollTop() > 0) {
      this.resetWheelState();
      return;
    }
    if (event.deltaY >= 0) {
      this.resetWheelState();
      return;
    }

    this.wheelDistance += Math.abs(event.deltaY);
    this.updateIndicator(Math.min(this.wheelDistance, this.pullToRefreshMax), this.wheelDistance >= this.pullToRefreshThreshold);
    if (event.cancelable) {
      event.preventDefault();
    }

    if (this.wheelDistance >= this.pullToRefreshThreshold) {
      this.refreshing = true;
      this.updateIndicator(this.pullToRefreshThreshold, true, true);
      this.zone.run(() => this.refresh.emit());
      this.resetWheelState();
      return;
    }

    if (this.wheelResetTimer) {
      clearTimeout(this.wheelResetTimer);
    }
    this.wheelResetTimer = setTimeout(() => this.resetWheelState(), 300);
  }

  private resetWheelState(): void {
    this.wheelDistance = 0;
    if (this.wheelResetTimer) {
      clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = null;
    }
    if (!this.refreshing) {
      this.updateIndicator(0, false);
    }
  }

  private updateIndicator(distance: number, ready: boolean, refreshing = false): void {
    if (!this.spacer) {
      return;
    }
    this.spacer.style.height = `${distance}px`;
    this.spacer.classList.toggle('ptr-ready', ready);
    this.spacer.classList.toggle('ptr-refreshing', refreshing);
  }

  private createSpacer(): HTMLElement {
    const spacer = this.renderer.createElement('div');
    this.renderer.addClass(spacer, 'ptr-spacer');
    const spinner = this.renderer.createElement('div');
    this.renderer.addClass(spinner, 'ptr-spinner');
    this.renderer.appendChild(spacer, spinner);
    return spacer;
  }

  private getScrollElement(host: HTMLElement, target?: HTMLElement | null): HTMLElement {
    if (!this.isBrowser) {
      return host;
    }

    if (target) {
      const scrollable = this.findScrollableAncestor(target, host);
      if (scrollable) {
        return scrollable;
      }
    }
    if (host.scrollHeight > host.clientHeight) {
      return host;
    }
    return (document.scrollingElement || document.documentElement) as HTMLElement;
  }

  private getScrollTop(): number {
    if (!this.isBrowser) {
      return this.scrollElement?.scrollTop || 0;
    }

    return Math.max(
      this.scrollElement?.scrollTop || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0,
      window.scrollY || 0
    );
  }

  private findScrollableAncestor(target: HTMLElement, host: HTMLElement): HTMLElement | null {
    if (!this.isBrowser) {
      return null;
    }

    let current: HTMLElement | null = target;
    while (current && current !== host) {
      const style = getComputedStyle(current);
      const overflowY = style.overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }
}
